import type { Table } from 'dexie';

/**
 * The tombstone convention every synced table shares — "LiftX Sync Blueprint"
 * §7. A delete never physically removes a row from Dexie; it stamps
 * `deletedAt` instead, so the row survives to be pushed to `liftx-sync` on
 * the next sync cycle (the server's own soft-delete column, per
 * `migrations/sync/0001_sync_schema.sql`). Every repository read path that
 * returns rows to the UI or to other business logic must filter through
 * `isLive` so a tombstoned row behaves exactly as if it were actually gone.
 *
 * The purge (turning a confirmed-pushed tombstone into a real Dexie delete)
 * is the sync engine's job, not any repository's — see `src/data/sync/`.
 */

export function isLive<T extends { deletedAt?: number | null }>(row: T): boolean {
  return row.deletedAt == null;
}

/** Stamps `deletedAt` on one row by primary key — the tombstone equivalent of `table.delete(id)`. */
export async function tombstoneById<T extends { deletedAt?: number | null }>(
  table: Table<T, string>,
  id: string,
  now: number,
): Promise<void> {
  await table.update(id, { deletedAt: now } as never);
}

/** Stamps `deletedAt` on every row in `rows` — the tombstone equivalent of a `.where(...).delete()` cascade. */
export async function tombstoneRows<T extends { id: string; deletedAt?: number | null }>(
  table: Table<T, string>,
  rows: readonly T[],
  now: number,
): Promise<void> {
  await Promise.all(rows.map((row) => table.update(row.id, { deletedAt: now } as never)));
}
